import {
    MAX_TIMEOUT_SECONDS,
    MIN_TIMEOUT_SECONDS,
    PUBLISH_STATUS,
    PUBLISH_STATUS_MESSAGES,
    TRANSLATION_STRUCTURE_TOP_LEVEL_KEYS,
    validConsentConfigModes,
} from './constants';
import { intToHexString, parseColor } from '../common/helpers/Color';

import cloneDeep from 'lodash/cloneDeep';
import { filterUnique } from '../common/helpers/Array';
import { flatten } from '../common/helpers/flat';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import set from 'lodash/set';
import startCase from 'lodash/startCase';
import themePalette from '../common/theme/palette';

export const getPublishStatusText = publishStatus =>
    PUBLISH_STATUS_MESSAGES[publishStatus] || startCase(publishStatus || '');

export const isPublishing = publishStatus => {
    switch (publishStatus) {
        case PUBLISH_STATUS.BUILDING:
        case PUBLISH_STATUS.IN_PROGRESS:
        case PUBLISH_STATUS.QUEUED: {
            return true;
        }
    }
    return false;
};

const createTranslations = (translations, isLegacy) => {
    const flatMap = flatten(translations);
    return Object.entries(flatMap).reduce((overrides, [key, value]) => {
        const keys = key.split('.');
        let path;
        let localeCode;

        if (isLegacy) {
            // ensures categories.{localeCode}.messaging legacy translations are included
            if (keys[0] === 'categories' && keys[keys.length - 1] !== 'messaging') {
                return overrides;
            }
            // Legacy leaves are `{field}.{locale}.{section}`. Anything else is a stray key; reshaping
            // it would move it to a new path on every save until the backend rejects it.
            const topLevelKey = keys[keys.length - 1];
            const legacyLocale = keys[keys.length - 2];
            if (
                !TRANSLATION_STRUCTURE_TOP_LEVEL_KEYS.includes(topLevelKey) ||
                // eslint-disable-next-line security/detect-unsafe-regex
                !/^[a-z]{2}(-[a-z]{2})?$/.test(legacyLocale)
            ) {
                return overrides;
            }
            localeCode = legacyLocale;
            path = [topLevelKey, ...keys.slice(0, -2)];
        } else {
            // skip categories.{localeCode}.messaging translations as these as legacy translations
            if (keys[0] === 'categories' && keys[keys.length - 1] === 'messaging') {
                return overrides;
            }
            localeCode = keys.pop();
            path = keys;
        }

        if (localeCode) {
            const localization = overrides[localeCode] || {};
            set(localization, path, value);
            overrides[localeCode] = localization;
        }
        return overrides;
    }, {});
};

export const createTranslationOverridesFromState = state => {
    // extract translations based on the current structure
    const translations = Object.keys(state).reduce((acc, key) => {
        if (TRANSLATION_STRUCTURE_TOP_LEVEL_KEYS.includes(key)) {
            acc[key] = state[key];
        }
        return acc;
    }, {});
    const translationOverrides = createTranslations(translations, false);

    // extract legacy translations - this is likely to occur if a config is reverted to a previous version that existed before the migration to the new translations structure
    const legacyTranslations = Object.keys(state).reduce((acc, key) => {
        if (key === 'categories' || !TRANSLATION_STRUCTURE_TOP_LEVEL_KEYS.includes(key)) {
            acc[key] = state[key];
        }
        return acc;
    }, {});
    let legacyTranslationOverrides = {};
    if (!isEmpty(legacyTranslations)) {
        legacyTranslationOverrides = createTranslations(legacyTranslations, true);
    }

    if (isEmpty(legacyTranslationOverrides)) {
        return translationOverrides;
    } else {
        return merge(legacyTranslationOverrides, translationOverrides);
    }
};

// Flags translations the user cleared (had a value in `baseline`, now empty) by
// setting them to `null`. transformTranslationsForRequest later sends those as ""
// so the backend resets them to the platform default. Always-empty leaves are
// left untouched and omitted from the request.
//
// A single space " " is a distinct, intentional value (a blank override that
// renders empty on the banner rather than falling back to the default). It is
// truthy, so it is never treated as a clear here and is preserved on the wire.
export const applyTranslationClears = (current = {}, baseline = {}) => {
    const flatCurrent = flatten(current);
    const flatBaseline = flatten(baseline);
    const result = cloneDeep(current);

    Object.entries(flatBaseline).forEach(([key, baselineValue]) => {
        if (baselineValue && !flatCurrent[key]) {
            set(result, key, null);
        }
    });

    return result;
};

// Reshapes locale-first form state into the path-first, locale-leaf shape the
// backend expects, applying the clearing contract per leaf:
//   - undefined / "" -> omit (untouched field; backend keeps whatever it has)
//   - null           -> "" (clear marker from applyTranslationClears; resets to default)
//   - " "            -> " " (intentional blank override; preserved verbatim)
//   - any other value-> sent verbatim
export const transformTranslationsForRequest = translations => {
    const flatTranslations = flatten(translations);
    return Object.entries(flatTranslations).reduce((transformed, [key, value]) => {
        if (value === undefined || value === '') {
            return transformed;
        }

        const [localeCode, ...pathParts] = key.split('.');
        set(transformed, [...pathParts, localeCode], value === null ? '' : value);

        return transformed;
    }, {});
};

// The CMP emits '-' only for an absent notice (false is 'N'). With USPv1 on, an unconfirmed notice
// sends usp: null as before; when off, or back on from a stored off, notified: null clears it.
export const toUspPayload = (usp, storedUsp) => {
    const notified = usp.notified ? true : null;
    if (usp.enabled === false) {
        return { enabled: false, notified };
    }
    if (notified) {
        return { ...usp, notified };
    }
    return storedUsp?.enabled === false ? { enabled: true, notified } : null;
};

export const createConsentConfigPayloadFromState = state => {
    const { configuration, domain, domains = [], mode, ...remain } = state;
    const {
        additionalLinks,
        iab = {},
        iframeBlocking,
        localStorageBlocking,
        palette,
        storagePolicyHref,
        timeoutSeconds,
        translations = {},
    } = configuration;
    const newConfiguration = {
        ...configuration,
        translations: transformTranslationsForRequest(translations),
        iframeBlocking: iframeBlocking || '',
        localStorageBlocking: localStorageBlocking || '',
        storagePolicyHref: encodeURI(storagePolicyHref),
        additionalLinks: additionalLinks.map(([text, href]) => [text, encodeURI(href)]),
        iab: {
            ...iab,
            notified: undefined,
            signatory: undefined,
        },
        palette,
        timeoutSeconds: Math.min(
            MAX_TIMEOUT_SECONDS,
            Math.max(MIN_TIMEOUT_SECONDS, timeoutSeconds || 10)
        ),
    };

    return {
        ...remain,
        mode: validConsentConfigModes.includes(mode) ? mode : validConsentConfigModes[0], // TODO: Add check for valid rules
        domains: domain ? [domain, ...domains].filter(filterUnique) : domains,
        configuration: newConfiguration,
    };
};

export const makeComplianceModeChipInlineStyles = (mode, darker = false) => {
    let color = darker ? themePalette.info.light : themePalette.data2.main;
    let textColor = themePalette.text.primary;

    switch (mode) {
        case '': {
            color = themePalette.tertiary.main;
            textColor = themePalette.tertiary.contrastText;
            break;
        }

        case 'debug': {
            color = darker ? themePalette.common.gray : themePalette.common.neutral;
            break;
        }

        case 'permissive': {
            color = darker ? themePalette.success.dark : themePalette.success.main;
            break;
        }

        default: {
            break;
        }
    }

    return {
        backgroundColor: `#${intToHexString.apply(null, parseColor(color))}`,
        borderColor: `#${intToHexString.apply(null, parseColor(color))}`,
        color: `#${intToHexString.apply(null, parseColor(textColor))}`,
    };
};

export const mapOrgToOption = ({ name, orgId }) => ({
    label: name,
    value: orgId,
});
